import { ZPlayerUpdate } from '@daymade/api-contracts/player/models';
import GenericModal from 'components/GenericModal';
import RoundedButton from 'components/RoundedButton';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { playerApiClient } from 'utils/apiClient';
import { ASSET_PREFIX } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import { validEmail } from 'utils/RegistrationValidator';

interface IChangeEmailPopup {
  currentEmailAddress?: string;
  onClose: () => void;
}
const ChangeEmailPopup = observer(
  ({ currentEmailAddress, onClose }: IChangeEmailPopup) => {
    const { displayToast } = useUIStore();
    const { currentUser, updateCurrentUser } = useUserStore();

    const [newEmailAddressText, setNewEmailAddressText] = useState<string>('');
    const [ctaLoading, setCtaLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    if (!currentEmailAddress) return null;

    return (
      <GenericModal onClose={onClose} noParagraphStyleOverrides>
        <img
          src={`${ASSET_PREFIX}/images/enter/change-email-icon.png`}
          alt=""
          style={{
            width: 80,
            height: 80,
          }}
        />

        <p
          style={{ marginTop: '1em' }}
          className="aboutUsTypographyHeaderMedium"
        >
          Change email
        </p>

        <input
          type="text"
          autoComplete="username"
          autoCorrect="off"
          value={currentEmailAddress}
          className="emailField"
          disabled
          style={{
            color: '#777',
          }}
        />

        <input
          placeholder="New email address"
          type="text"
          autoComplete="username"
          autoCorrect="off"
          onChange={(e) => {
            setNewEmailAddressText(e.target.value);
            setError(null);
          }}
          value={newEmailAddressText}
          className="emailField"
        />

        {error && (
          <div
            style={{
              boxShadow: '0 0 0 1px #e0b4b4 inset, 0 0 0 0 transparent',
              backgroundColor: '#fff6f6',
              color: '#9f3a38',
              padding: '0.5em 0.75em',
              borderRadius: '4px',
              marginTop: '0.5em',
              fontSize: '0.7em',
            }}
          >
            {error}
          </div>
        )}

        <RoundedButton
          rebrandDm2025
          onClick={async () => {
            const email = newEmailAddressText.toLowerCase().trim();

            setError(null);
            setCtaLoading(true);

            try {
              // First, check that the email is valid
              if (!validEmail(email)) {
                setError(`"${email}" is not a valid email address.`);
                return;
              }

              // The server only lets us update email addresses if the user does not have any social logins
              if (currentUser && !currentUser.hasSocialLogin) {
                const updatePlayerRequest: Zod.TypeOf<typeof ZPlayerUpdate> = {
                  firstName: currentUser.firstName,
                  lastName: currentUser.lastName,
                  phoneNumber: currentUser.phoneNumber,
                  gender: currentUser.gender,
                  dateOfBirth: currentUser.dateOfBirth,
                  interests: currentUser.interests,
                  emailAddress: email,
                };

                const result = await playerApiClient.auth.updateDetails({
                  body: {
                    player: updatePlayerRequest,
                  },
                });

                if (result.status === HttpStatusCode.OK) {
                  displayToast({
                    title: `Successfully updated email address to ${email}`,
                    color: '#00ff00',
                    timeout: 7000,
                  });

                  await updateCurrentUser();
                  onClose();
                } else {
                  setError(result.body.message);
                }
              } else {
                setError(
                  'You cannot update your email, as you are using a social login with your account',
                );
              }
            } finally {
              setCtaLoading(false);
            }
          }}
          loading={ctaLoading}
          style={{ marginTop: '1.2em', width: '100%', maxWidth: 250 }}
        >
          Save Changes
        </RoundedButton>

        <button className="changeEmailCancelButton" onClick={onClose}>
          Cancel
        </button>

        <style jsx>
          {`
            :global(.emailField) {
              border-radius: 25px !important;
              padding: 0.5em 1em;
              font: inherit;
              font-weight: 600;
              border: 2px solid lightgray;
              margin: 0.3em 0;
              width: 100%;
              max-width: 250px;
              font-size: 16px;
              outline: none;
            }

            :global(.changeEmailCancelButton) {
              padding: 1.6em 0 0;
              background-color: transparent;
              color: inherit;
              font-weight: 700;
              font-size: 1.1em;
              text-decoration: underline;
            }

            :global(.changeEmailCancelButton:hover) {
              background-color: transparent;
            }
          `}
        </style>
      </GenericModal>
    );
  },
);

export default ChangeEmailPopup;
